import type { Entry } from "@schema/catalog.ts";
import { useProgressStore } from "@/state/progress.ts";
import styles from "./EntryCard.module.css";
import { CharacterAvatars } from "./entry/CharacterAvatars.tsx";
import { Poster } from "./entry/Poster.tsx";
import { QualityBadge } from "./entry/QualityBadge.tsx";
import { RuntimeChip } from "./entry/RuntimeChip.tsx";
import { StarRating } from "./entry/StarRating.tsx";
import { ThreadChips } from "./entry/ThreadChips.tsx";
import { TierBadge } from "./entry/TierBadge.tsx";
import { WatchToggle } from "./entry/WatchToggle.tsx";
import { WhyItMatters } from "./entry/WhyItMatters.tsx";

/**
 * "S7 E8", or "S2" for a whole season, or nothing at all.
 *
 * Kept out of the title string so it can be styled and read separately — a
 * screen reader announcing "Pups Save a Hum-Dino S7 E8" as one run of text is
 * worse than two.
 */
function episodeCode(entry: Entry): string | null {
  const { seasonNumber, episodeNumber } = entry.source;
  if (seasonNumber === null) return null;
  return episodeNumber === null ? `S${seasonNumber}` : `S${seasonNumber} E${episodeNumber}`;
}

export function EntryCard({ entry }: { entry: Entry }) {
  const watchedOn = useProgressStore((s) => s.watched[entry.id]);
  const watched = watchedOn !== undefined;

  return (
    <article className={`${styles.card} ${watched ? styles.watched : ""}`} data-entry-id={entry.id}>
      <span className={styles.orderNo} aria-hidden="true">
        {entry.orderIndex + 1}
      </span>
      <Poster entry={entry} />
      <div className={styles.body}>
        <div className={styles.titleRow}>
          <h3 className={styles.title}>
            {entry.title} <span className={styles.year}>{entry.year}</span>
            {/* S7 E8 next to the year, because a title alone is not enough to
             * find one episode among hundreds in a streaming app's list. Only
             * rendered when the numbers exist, so films are unaffected. */}
            {episodeCode(entry) && <span className={styles.episodeCode}>{episodeCode(entry)}</span>}
          </h3>
          <div className={styles.watchRow}>
            {/* Only once it's watched: rating something you haven't seen is a
             * different question, and answering both with one control would
             * make neither mean anything. */}
            {watched && <StarRating entryId={entry.id} title={entry.title} />}
            <WatchToggle entryId={entry.id} watched={watched} title={entry.title} />
          </div>
        </div>
        <div className={styles.badges}>
          <TierBadge tier={entry.curation.tier} />
          <QualityBadge quality={entry.quality} tier={entry.curation.tier} />
          <RuntimeChip runtime={entry.runtime} kind={entry.kind} />
        </div>
        <WhyItMatters curation={entry.curation} />
        <div className={styles.meta}>
          <ThreadChips links={entry.curation.threads} />
          <CharacterAvatars links={entry.curation.characters} />
        </div>
      </div>
    </article>
  );
}
